/**
 *
 * InvoiceDetailDetections
 *
 */

import React, { useEffect, useMemo, useCallback, useLayoutEffect, useState, useRef } from 'react';
import _ from 'lodash';

// api
import {
  getInvoiceDetailTypesOrder,
  InvoiceDetailTypesOrderItem,
  InvoicePredictionDetailType,
  InvoiceDetailType,
  InvoiceDetailContactType,
  InvoiceDetailContactAddressType,
  isSingularType,
  InvoiceAdditionalPropertiesType,
  InvoiceDetailSupplierType,
  InvoiceDetailClientType,
  FirstInvoiceDetails,
  OtherDetails,
} from 'lib/api/invoiceDetails';

import {
  FlatInvoiceDetail,
  FlatInvoiceDetails,
  flattenDetectInvoiceResponse,
} from 'lib/api/flatInvoiceDetails';

import { forceReRender } from '@storybook/react';
import { useIntl } from 'react-intl';
import store from 'store';

// ui
import { Flex, IconButton } from '@domonda/ui';
import { AccordionToggle, AddDocument, ArrowDown} from 'lib/domonda-icons';

// translations
import { FormattedMessage } from 'react-intl';

// parts
import { InvoiceDetailDetectionsFormField } from './InvoiceDetailDetectionsFormField';
import { FormValues } from '../InvoiceDetail/selectors';
import { OCRTextPageDetection } from 'modules/OCRTextPage';

// decorate
import { decorate, Decorate } from './decorate';
import { useFormContext } from '@domonda/react-form';
import { FormTag } from '@domonda/form';
import { getConfigFromStore } from 'lib/api/customerConfig';
import { useLocale } from 'lib/intl';
import { returnClientIdMessage, returnSupplierMessage } from 'core/translations/IdError';

export interface InvoiceDetailDetectionsProps {
  onThreshold: (name: string, above: boolean) => void;
  saving: boolean;
  badClientId?: string;
  badSupplierId?: string;
  highlightedDetection: OCRTextPageDetection | null;
  onHighlightDetection: (detection: OCRTextPageDetection | null) => void;
  containerId?: string;
  pagesWithDetections?: any;
  lineItems?: [any];
}

const InvoiceDetailDetections: React.FC<InvoiceDetailDetectionsProps & Decorate> = (props) => {
  const {
    classes,
    badClientId,
    badSupplierId,
    saving,
    highlightedDetection,
    onHighlightDetection,
    onThreshold,
    containerId,
    pagesWithDetections,
    lineItems,
  } = props;

  const form = useFormContext<FormValues>();
  const { locale } = useLocale();
  // set german locale
  const { setLocale } = useLocale();
  setLocale('de');
  // flat detect invoice response necessary for creating fields
  const [
    { invoiceDetailsMap, invoiceDetailsGroupsMap },
    setFlattenedDetectInvoiceResponse,
  ] = useState<ReturnType<typeof flattenDetectInvoiceResponse>>({
    invoiceDetailsMap: [],
    invoiceDetailsGroupsMap: [],
  });
  const { formatMessage } = useIntl();

  useLayoutEffect(
    () =>
      setFlattenedDetectInvoiceResponse(
        flattenDetectInvoiceResponse(form.values.Invoice.DetectInvoiceResponse),
      ),
    [],
  );
  const refs = useMemo(
    () =>
      [
        ...invoiceDetailsMap,
        ...invoiceDetailsGroupsMap.flatMap(({ InvoiceDetailsMap }) => InvoiceDetailsMap),
      ].reduce(
        (result, { Id }) => ({
          ...result,
          [Id]: {
            current: null,
          },
        }),
        {} as Record<FlatInvoiceDetail['Id'], React.MutableRefObject<{ focus: () => void } | null>>,
      ),
    [invoiceDetailsMap, invoiceDetailsGroupsMap],
  );
  useEffect(() => {
    if (highlightedDetection) {
      const { current } = refs[highlightedDetection.Id];
      if (current) current.focus();
    }
  }, [highlightedDetection]);
    const invoiceDets = [
    {
      "Name": "DeliveryDate",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 8,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "GrandTotalAmount",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 16,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.967,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "InvoiceDate",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 64,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.489,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "NetTotalAmount",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 256,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "InvoiceId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 1024,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.011,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "DocumentType",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 8192,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0.798,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Iban",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 16384,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Bic",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 32768,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "InvoiceCurrency",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 524288,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0.413,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "DeliveryNoteId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 1048576,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "CustomerId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 2097152,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "UId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 8388608,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "SenderOrderId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 16777216,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "ReceiverOrderId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 33554432,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "SenderOrderDate",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 67108864,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "ReceiverVatId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 2048,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "SenderVatId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 512,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "ReceiverOrderDate",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 134217728,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "VatTotalAmount",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 1073741824,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender",
      "PropertyPath": "Sender",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": false
    },
    {
      "Name": "Sender.Name",
      "PropertyPath": "Sender.Name",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Address",
      "PropertyPath": "Sender.Address",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": false
    },
    {
      "Name": "Sender.Address.Street",
      "PropertyPath": "Sender.Address.Street",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Address.ZipCode",
      "PropertyPath": "Sender.Address.ZipCode",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Address.City",
      "PropertyPath": "Sender.Address.City",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Address.Country",
      "PropertyPath": "Sender.Address.Country",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.WebsiteUrl",
      "PropertyPath": "Sender.WebsiteUrl",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Email",
      "PropertyPath": "Sender.Email",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Phone",
      "PropertyPath": "Sender.Phone",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Fax",
      "PropertyPath": "Sender.Fax",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver",
      "PropertyPath": "Receiver",
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": false
    },
    {
      "Name": "Receiver.Name",
      "PropertyPath": "Receiver.Name",
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Address",
      "PropertyPath": "Receiver.Address",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": false
    },
    {
      "Name": "Receiver.Address.Street",
      "PropertyPath": "Receiver.Address.Street",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Address.ZipCode",
      "PropertyPath": "Receiver.Address.ZipCode",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Address.City",
      "PropertyPath": "Receiver.Address.City",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Address.Country",
      "PropertyPath": "Receiver.Address.Country",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.WebsiteUrl",
      "PropertyPath": "Receiver.WebsiteUrl",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Email",
      "PropertyPath": "Receiver.Email",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Phone",
      "PropertyPath": "Receiver.Phone",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Fax",
      "PropertyPath": "Receiver.Fax",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "VatGroup",
      "PropertyPath": "PredictionGroups[0]",
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": true,
      "HasScore": false
    },
    {
      "Name": "VatGroup.VatRate",
      "PropertyPath": "PredictionGroups[0].InvoiceDetailTypePredictions",
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.95,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "VatGroup.VatAmount",
      "PropertyPath": "PredictionGroups[0].InvoiceDetailTypePredictions",
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.95,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "VatGroup.NetAmount",
      "PropertyPath": "PredictionGroups[0].InvoiceDetailTypePredictions",
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.95,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    }
  ];
  const orderItems = useMemo(getInvoiceDetailTypesOrder, []);
  orderItems[0] = orderItems[0].filter((item) => {
    //if (item.type === 'Client') return getConfigFromStore().IsClientVisible;
    if (item.type === 'Client') return true;
    //else if (item.type === 'Supplier') return getConfigFromStore().IsSupplierVisible;
    else if (item.type === 'Supplier') return true;
    //if (item.type === 'AdditionalProperties') return getConfigFromStore().IsClientVisible;
    if (item.type === 'AdditionalProperties') return true;
    //const confItems = getConfigFromStore().InvoiceDetails;
    const confItems = invoiceDets;
    const confDetail = confItems.find((el) => el.Name === item.type);
    if (!confDetail) return true;
    return confDetail.Priority === 'MustHave' || confDetail.Priority === 'NiceToHave';
  });
  orderItems[1] = orderItems[1].filter((item) => {
    //if (item.type === 'Client') return getConfigFromStore().IsClientVisible;
    if (item.type === 'Client') return true;
    //else if (item.type === 'Supplier') return getConfigFromStore().IsSupplierVisible;
    else if (item.type === 'Supplier') return true;
    //const confItems = getConfigFromStore().InvoiceDetails;
    const confItems = invoiceDets;
    const confDetail = confItems.find((el:any) => el.Name === item.type);
    if (!confDetail) return true;
    return confDetail.Priority === 'MustHave' || confDetail.Priority === 'NiceToHave';
  });
  //const confItems = getConfigFromStore().InvoiceDetails;
  const confItems = invoiceDets;
  confItems.forEach((confItem) => {
    const nameSplit = confItem.Name.split('.');
    if (nameSplit.length > 1) {
      const itemOneIndex = orderItems[0].findIndex((el) => el.type === nameSplit[0]);
      if (itemOneIndex !== -1) {
        const typeIndex = orderItems[0][itemOneIndex].items!.findIndex((item) => {
          if (!item) return false;
          return item.type === nameSplit[1];
        });
        if (typeIndex !== -1) {
          if (confItem.Priority !== 'MustHave' && confItem.Priority !== 'NiceToHave')
            delete orderItems[0][itemOneIndex].items![typeIndex];
        }
      } else {
        const itemTwoIndex = orderItems[1].findIndex((el) => el.type === nameSplit[0]);
        if (itemTwoIndex !== -1) {
          const typeIndex = orderItems[1][itemTwoIndex].items!.findIndex((item) => {
            if (!item) return false;
            return item.type === nameSplit[1];
          });
          if (typeIndex !== -1) {
            if (confItem.Priority !== 'MustHave' && confItem.Priority !== 'NiceToHave')
              delete orderItems[1][itemTwoIndex].items![typeIndex];
          }
        }
      }
    }
  });
  // adding a new array item
  const addFormValue = useCallback(
    (
      type:
        | InvoicePredictionDetailType
        | InvoiceDetailType
        | InvoiceDetailContactType
        | InvoiceAdditionalPropertiesType
        | InvoiceDetailSupplierType
        | FirstInvoiceDetails
        | string
        | OtherDetails
        | InvoiceDetailClientType
        | InvoiceDetailContactAddressType,
      path = '',
    ) => {
      let nextValues: FormValues | undefined = undefined;
      const predictionGroup = form.values.Invoice.DetectInvoiceResponse.PredictionGroups.find(
        ({ TypeName }) => TypeName === type,
      );
      if (
        type === 'NetAmount' ||
        type === 'VatAmount' ||
        type === 'VatRate' ||
        type === 'DiscountDate' ||
        type === 'DiscountDuration' ||
        type === 'DiscountPercent' ||
        type === 'DueDateDate' ||
        type === 'DueDateDuration' ||
        type === 'BankCode' ||
        type === 'BankAccount'
      ) {
        nextValues = {
          ...form.values,
          Invoice: {
            ...form.values.Invoice,
            DetectInvoiceResponse: {
              ...form.values.Invoice.DetectInvoiceResponse,
              PredictionGroups: [...form.values.Invoice.DetectInvoiceResponse.PredictionGroups],
            },
          },
        };
        const pathSplit = path.split('.');
        const groupPath = pathSplit.slice(0, 3).join('.');
        const typePath = pathSplit.slice(0, pathSplit.length - 1).join('.');
        _.get(nextValues, groupPath).InvoiceDetailTypePredictions.push({
          Type: _.get(nextValues, typePath).Type,
          TypeName: type,
          Value: '',
          Score: 1, // beacuse the user has performed the input
          Width: 0,
          Height: 0,
          X: 0,
          Y: 0,
        });
      }
      if (predictionGroup) {
        nextValues = {
          ...form.values,
          Invoice: {
            ...form.values.Invoice,
            DetectInvoiceResponse: {
              ...form.values.Invoice.DetectInvoiceResponse,
              PredictionGroups: [
                ...form.values.Invoice.DetectInvoiceResponse.PredictionGroups,
                {
                  Type: predictionGroup.Type,
                  TypeName: type as InvoicePredictionDetailType,
                  InvoiceDetailTypePredictions: predictionGroup.InvoiceDetailTypePredictions.map(
                    ({ Type, TypeName }) => ({
                      Type,
                      TypeName,
                      Value: '',
                      Score: 1, // beacuse the user has performed the input
                      Width: 0,
                      Height: 0,
                      X: 0,
                      Y: 0,
                    }),
                  ),
                },
              ],
            },
          },
        };
      } else {
        const pathSplit = path.split('.');
        const name = pathSplit.slice(0, pathSplit.length - 1);
        let namePath = name.join('.');
        namePath = namePath.slice(0, namePath.indexOf('['));
        if ((path && pathSplit[2] === 'Sender') || pathSplit[2] === 'Receiver') {
          nextValues = {
            ...form.values,
            Invoice: {
              ...form.values.Invoice,
              DetectInvoiceResponse: {
                ...form.values.Invoice.DetectInvoiceResponse,
                [pathSplit[2]]: {
                  ...(form.values.Invoice.DetectInvoiceResponse as any)[pathSplit[2]],
                  [type]: [
                    ..._.get(form.values, namePath),
                    {
                      Name: type,
                      Height: 0,
                      Width: 0,
                      X: 0,
                      Y: 0,
                      Score: 1,
                      Value: '',
                      Text: '',
                    },
                  ],
                },
              },
            },
          };
        } else {
          const prediction = form.values.Invoice.DetectInvoiceResponse.InvoiceDetailTypePredictions.find(
            ({ TypeName }) => TypeName === type,
          );
          if (prediction) {
            nextValues = {
              ...form.values,
              Invoice: {
                ...form.values.Invoice,
                DetectInvoiceResponse: {
                  ...form.values.Invoice.DetectInvoiceResponse,
                  InvoiceDetailTypePredictions: [
                    ...form.values.Invoice.DetectInvoiceResponse.InvoiceDetailTypePredictions,
                    {
                      Type: prediction.Type,
                      TypeName: type as InvoicePredictionDetailType,
                      Value: '',
                      Score: 1, // beacuse the user has performed the input
                      Width: 0,
                      Height: 0,
                      X: 0,
                      Y: 0,
                    },
                  ],
                },
              },
            };
          }
        }
      }

      if (nextValues) {
        form.plumb.next(
          {
            ...form.plumb.state,
            values: nextValues, // default values stay the same!
          },
          FormTag.VALUES_CHANGE,
        );
        setFlattenedDetectInvoiceResponse(
          flattenDetectInvoiceResponse(nextValues.Invoice.DetectInvoiceResponse),
        );
      }
    },
    [form],
  );

  const InvoiceDetailDetectionsItem = useCallback<
    React.FC<{
      orderItem: InvoiceDetailTypesOrderItem;
      details: FlatInvoiceDetails;
      autoFocus?: boolean | undefined;
      additionalProperties?: boolean | undefined;
    }>
  >(
    ({ orderItem, details, autoFocus = undefined, additionalProperties = false }) => (
      <>
        {details
          .filter(({ Type }) => {
            if (orderItem.type === InvoiceDetailContactType.Address)
              return Object.values(InvoiceDetailContactAddressType).includes(
                Type as InvoiceDetailContactAddressType,
              );
            return Type === orderItem.type;
          })
          .map((detail) => {

            // hide details not neccessery for demo page
            if (detail.Type === 'IsrReference' || detail.Type === 'IsrSubscriber' || detail.Type === 'KId') {
              return;
            }

            const idSplit = detail.Id.split('-');
            let columnName = '';
            if (idSplit.length === 3) columnName = idSplit[1];
            else columnName = idSplit[idSplit.length - 1];
            const isFieldArray = columnName === 'Ibans' || columnName === 'Uids';
            let shouldBeDisplayed = true;
            if (isFieldArray) {
              const val = _.get(form.values, detail.Path);
              if (val) shouldBeDisplayed = true;
              else if (Number(detail.Id[detail.Id.length - 1]) > 0) shouldBeDisplayed = false;
            }
            return (
              <Flex
                style={{ width: additionalProperties ? 'auto' : '100%', display: shouldBeDisplayed ? 'block' : 'none' }}
                key={detail.Id}
                id={detail.Id}
                item
              >
                <Flex container align="center">
                  <Flex item flex={1}>
                    <InvoiceDetailDetectionsFormField
                      autoFocus={autoFocus}
                      onThreshold={onThreshold}
                      refs={refs}
                      flatInvoiceDetail={detail}
                      disabled={saving}
                      onHighlightDetection={onHighlightDetection}
                      readOnly={detail.Disabled}
                      additionalProperties={additionalProperties}
                      pagesWithDetections={pagesWithDetections}
                    />
                  </Flex>
                  {/*
                  {!isSingularType(orderItem.type, detail.Path) && (
                    <Flex item>
                      <IconButton
                        className={classes.itemAdd}
                        onClick={() => addFormValue(orderItem.type, detail.Path)}
                      >
                        <AddDocument />
                      </IconButton>
                    </Flex>
                  )} */}
                </Flex>
              </Flex>
            );
          })}
      </>
    ),
    [refs, saving, onHighlightDetection],
  );
  const shouldAutoFocus = (index: number, itemIndex?: number) => {
    // Autofocus on first item if there are no AdditionalProperties
    if (orderItems[0][0].type === 'AdditionalProperties') {
      return index === 1 && (!itemIndex || itemIndex === 0);
    } else return index === 0 && (!itemIndex || itemIndex === 0);
  };

  //const confi = getConfigFromStore();
  //const removeGuiSplittingArea = confi ? confi.RemoveGuiSplittingArea : false;
  const removeGuiSplittingArea = true;
  let clientAndSupplierCount = 0;
  orderItems[1].forEach((item) => {
    //if (item.type === 'Supplier' && !confi.IsSupplierVisible) clientAndSupplierCount++;
    if (item.type === 'Supplier' && !true) clientAndSupplierCount++;
    //if (item.type === 'Client' && !confi.IsClientVisible) clientAndSupplierCount++;
    if (item.type === 'Client' && !true) clientAndSupplierCount++;
  });

  const dir =
    removeGuiSplittingArea &&
    (orderItems[1].length != 0 || orderItems[1].length - clientAndSupplierCount > 0)
      ? 'row'
      : 'column';

  const getSupplierColor = () => {
    //if (confi.MustHaveSupplierInValidation) {
    if (true) {
      if (
        form.values.Invoice.DetectInvoiceResponse.Supplier &&
        !form.values.Invoice.DetectInvoiceResponse.Supplier!.SupplierId
      ) return 'red'

      if(form.values.Invoice.DetectInvoiceResponse.Supplier === undefined) return '#FF0000' // Red color
        
      if (!form.values.Invoice.SupplierResult.SupplierId) return 'red';
      //else if (form.values.Invoice.SupplierResult.Score < confi.SupplierScoreThreshold)
      else if (form.values.Invoice.SupplierResult.Score < 0)
        return '#FCE300';
      else return '#92D050';
    } else return '#d8d8d8';
  };

  const getClientColor = () => {
    //if (confi.MustHaveClientInValidation) {
    if (true) {
      
      if(form.values.Invoice.DetectInvoiceResponse.Client === undefined) return '#FF0000' // Red color

      if (
        form.values.Invoice.DetectInvoiceResponse.Client &&
        !form.values.Invoice.DetectInvoiceResponse.Client!.Id
      )
        return 'red';
      else return '#92D050';
    } else return '#d8d8d8';
  };

  const getVatColor = (id: any ) => {
    if(!id.includes('VatGroup-')) return;
    //if(confi.InvoiceDetails.find(item => item.Name === 'VatGroup')!.Priority === 'NiceToHave') return '#cdcdcd'
    var vatNumber = parseInt(id.substr(id.length - 1))
    let color = '';
    form.values.Invoice.DetectInvoiceResponse.PredictionGroups[vatNumber].InvoiceDetailTypePredictions.forEach((item)=>{
      if(item.Value){
        if(item.Score < 0.95){
          color = color || '#FCE300' // Orange color 
          return
        }
      color =  color ? color : '#92D050' // Green color
      } else {
        color = 'red'
      }
    })
    return color
  };

  const inputsContainer = useRef<HTMLDivElement>(null);
  const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  const rightBorderPosition = (inputsContainer && inputsContainer.current) && (inputsContainer.current!.getBoundingClientRect().right);
  const docInfoWidth = rightBorderPosition && (vw - rightBorderPosition + 11);
  const [isDetailsVisible, setDetailsVisible] = 
  useState(sessionStorage.getItem('isDetailsVisible') === null ? true : (sessionStorage.getItem('isDetailsVisible') ==='true') ? true : false);
  
  const toggleDetails = (isOpen: boolean) => {
    setDetailsVisible(!isOpen)
    sessionStorage.setItem('isDetailsVisible', isOpen ? "false" : "true")
  }

  // Rerender on window width change
  const [windowWidth, setWindowWidth] = useState(0);
  const updateWindowDimensions = () => {
    const newWidth = window.innerWidth;
    setWindowWidth(newWidth);
  };
  window.addEventListener("resize", updateWindowDimensions);
  useEffect(() => {
      forceReRender()
  }, [windowWidth]);
  const invoiceContainerResponse = store.get('invoiceContainerResponse');

  const possibleFields = [
    'PositionNumber',
    'PositionId',
    'DeliveryId',
    'OrderId',
    'ItemId',
    'Description',
    'Unit',
    'DeliveryDate',
    'Quantity',
    'UnitPrice',
    'Discount',
    'VatRate',
    'TotalAmount',
  ];

  const getFoundColumns = () => {
    let foundColumns = [];
    lineItems.map(li => (
      possibleFields.map(pf => {
        if (li[pf].Value) {
          if (!foundColumns.includes(pf)) {
            foundColumns.push(pf);
          }
        }
    })));
    return foundColumns;
  };

  return (
    <>
    <Flex
      container
      direction={dir}
      noWrap={true}
      spacing={removeGuiSplittingArea ? 4 : 1}
      className={classes.root}
      ref={inputsContainer}
    >
      <Flex item container direction="column" spacing={1}>
        <Flex item container spacing={1}>
        <div>
          <div className={`${classes.additionalDetails} ${isDetailsVisible ? "" : classes.hideProperties }`} style={{width: `${docInfoWidth ? (docInfoWidth - 10 + 'px') : ""}`, display: 'block', height: 'auto', padding: '20px'}}>
            <p onClick={()=> toggleDetails(isDetailsVisible)} style={{cursor: 'pointer', display: 'flex', alignItems: 'center', width: '100%', color: '#4a4a4a', fontSize: '15px', fontWeight: 500, margin: '0 0 20px 0'}}><AccordionToggle style={{transition: 'all 0.4s', transform: `${isDetailsVisible ? '' : 'rotateZ(180deg)'}`}} /> {formatMessage({ id: `LINE_ITEMS` })}</p>
            <div style={{maxHeight: '20vh', overflow: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', color: '#4a4a4a', fontSize: '14px'}}>
                <thead>
                  <tr>
                    {getFoundColumns().map((fc, i) => (
                      <th key={i} className={classes.lineItemHeading}>{formatMessage({ id: `LINE_ITEM_FIELD.${fc}` })}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lineItems && lineItems.map((lineItem, i) => { // limit items to 3 as a teaser
                    return (
                      <tr key={i}>
                        {getFoundColumns().map((fc, i) => (
                          <td key={i} className={classes.lineItemCell}>{lineItem[fc].Value}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
          {orderItems[0].map((orderItem, index) => {
            if (orderItem.items != null) {
              return invoiceDetailsGroupsMap
                .filter(({ Type }) => Type === orderItem.type)
                .map(({ Id, InvoiceDetailsMap }) => {

                  // hide unnecessary details for demo purposes
                  if (
                    orderItem.type === 'DiscountGroup' ||
                    orderItem.type === 'BankGroup' ||
                    orderItem.type === 'DueDateGroup' ||
                    orderItem.type === 'Receiver'
                  ) {
                    return;
                  }

                  if(orderItem.type === "AdditionalProperties" && getConfigFromStore().IsClientVisible){ 
                    return (
                      <div key={Id}>
                        <ArrowDown className={`${classes.arrowIcon} ${isDetailsVisible ? '' : classes.rotateIcon}`} onClick={()=> toggleDetails(isDetailsVisible)} />
                        <div className={`${classes.additionalDetails} ${isDetailsVisible ? "" : classes.hideProperties }`} style={{width: `${docInfoWidth ? (docInfoWidth + 'px') : ""}`}}>
                          <div className={classes.containerIdWrapper}>
                            <p className={classes.containerIdTitle}>Container ID:</p>
                            <p className={classes.containerIdText}>{containerId}</p>
                          </div>
                          <div className={classes.itemWrapper}>
                            <p className={classes.containerIdTitle}>{formatMessage({ id: 'DOCUMENTS_CLIENT' })}:</p>
                            <p className={classes.containerIdText}>{invoiceContainerResponse.InvoiceContainers[0].CreatedBy}</p>
                          </div>
                          <div className={classes.innerContainer}>
                            {orderItem.items && 
                              orderItem.items.map((orderItem) => {
                                if(orderItem.type === 'ClientId') return
                                return(
                                  <InvoiceDetailDetectionsItem
                                    autoFocus={false} // dont autofocus on AdditionalProperties since they are read only
                                    key={Id + '-' + orderItem.type}
                                    orderItem={orderItem}
                                    details={InvoiceDetailsMap}
                                    additionalProperties={true}
                                  />
                              )})}
                          </div>
                        </div>
                      </div>
                  )}
                  return (
                    <Flex style={{ width: '100%', paddingRight: '0' }} key={Id} item>
                      <Flex container align="center">
                        <Flex item flex={1}>
                          <div
                            className={classes.details}
                            id={Id}
                            style={{
                              borderColor:
                                Id === 'Supplier'
                                  ? getSupplierColor()
                                  : Id === 'Client'
                                  ? getClientColor()
                                  : getVatColor(Id),
                            }}
                          >
                            <input
                              type="checkbox"
                              defaultChecked
                              id={`${Id}-C`}
                              className={classes.detailsToggler}
                            />
                            <Flex
                              item
                              container
                              component={({ children, ...rest }) => (
                                <label {...rest} htmlFor={`${Id}-C`}>
                                  {children}
                                </label>
                              )}
                              align="center"
                              justify="flex-end"
                              className={classes.detailsSummary}
                            >
                              <Flex item>
                                <FormattedMessage
                                  defaultMessage={orderItem.type}
                                  id={`INVOICE_DETAIL.${orderItem.type}`}
                                />
                              </Flex>

                              <Flex item>
                                <AccordionToggle />
                              </Flex>
                            </Flex>
                            <Flex
                              item
                              container
                              direction="column"
                              spacing={1}
                              className={classes.detailsContent}
                            >
                              {orderItem.items && 
                                orderItem.items.map((orderItem, itemIndex) => {
                                  return(
                                  <InvoiceDetailDetectionsItem
                                    autoFocus={shouldAutoFocus(index, itemIndex)}
                                    key={Id + '-' + orderItem.type}
                                    orderItem={orderItem}
                                    details={InvoiceDetailsMap}
                                  />
                                )})}
                            </Flex>
                            {Id === 'Client' && form.values.BadClientId && (
                              <p id="badClientId" style={{ color: 'red' }}>
                                {returnClientIdMessage(locale, badClientId)}
                              </p>
                            )}
                            {Id === 'Supplier' && form.values.BadSupplierId && (
                              <p id="badSupplierId" style={{ color: 'red' }}>
                                {returnSupplierMessage(locale, badSupplierId)}
                              </p>
                            )}
                          </div>
                        </Flex>
                        {!isSingularType(orderItem.type) && (
                          <Flex item>
                            <IconButton
                              className={classes.itemAdd}
                              onClick={() => addFormValue(orderItem.type)}
                            >
                            </IconButton>
                          </Flex>
                        )}
                      </Flex>
                    </Flex>
                  );
                });
            }

            return (
              <InvoiceDetailDetectionsItem
                autoFocus={shouldAutoFocus(index)}
                key={orderItem.type}
                orderItem={orderItem}
                details={invoiceDetailsMap}
              />
            );
          })}
        </Flex>
        <Flex item container spacing={1}>
          {orderItems[1].map((orderItem) => {
            if (orderItem.items != null) {
              return invoiceDetailsGroupsMap
                .filter(({ Type }) => Type === orderItem.type)
                .map(({ Id, InvoiceDetailsMap }) => {
                  return (
                    <Flex style={{ width: '100%' }} key={Id} item>
                      <Flex container align="center">
                        <Flex item flex={1}>
                          <div
                            className={classes.details}
                            id={Id}
                            style={{
                              borderColor:
                                Id === 'Supplier'
                                  ? getSupplierColor()
                                  : Id === 'Client'
                                  ? getClientColor()
                                  : '#d8d8d8',
                            }}
                          >
                            <input
                              type="checkbox"
                              defaultChecked
                              id={`${Id}-C`}
                              className={classes.detailsToggler}
                            />
                            <Flex
                              item
                              container
                              component={({ children, ...rest }) => (
                                <label {...rest} htmlFor={`${Id}-C`}>
                                  {children}
                                </label>
                              )}
                              align="center"
                              justify="flex-end"
                              className={classes.detailsSummary}
                            >
                              <Flex item>
                                <FormattedMessage
                                  defaultMessage={orderItem.type}
                                  id={`INVOICE_DETAIL.${orderItem.type}`}
                                />
                              </Flex>

                              <Flex item>
                                <AccordionToggle />
                              </Flex>
                            </Flex>

                            <Flex
                              item
                              container
                              direction="column"
                              spacing={1}
                              className={classes.detailsContent}
                            >
                              {orderItem.items &&
                                orderItem.items.map((orderItem) => (
                                  <InvoiceDetailDetectionsItem
                                    key={Id + '-' + orderItem.type}
                                    orderItem={orderItem}
                                    details={InvoiceDetailsMap}
                                  />
                                ))}
                            </Flex>
                            {Id === 'Client' && form.values.BadClientId && (
                              <p id="badClientId" style={{ color: 'red' }}>
                                {returnClientIdMessage(locale, badClientId)}
                              </p>
                            )}
                            {Id === 'Supplier' && form.values.BadSupplierId && (
                              <p id="badSupplierId" style={{ color: 'red' }}>
                                {returnSupplierMessage(locale, badSupplierId)}
                              </p>
                            )}
                          </div>
                        </Flex>
                        {!isSingularType(orderItem.type) && (
                          <Flex item>
                            <IconButton
                              className={classes.itemAdd}
                              onClick={() => addFormValue(orderItem.type)}
                            >
                            </IconButton>
                          </Flex>
                        )}
                      </Flex>
                    </Flex>
                  );
                });
            }

            return (
              <InvoiceDetailDetectionsItem
                key={orderItem.type}
                orderItem={orderItem}
                details={invoiceDetailsMap}
              />
            );
          })}
        </Flex>
      </Flex>
    </Flex>
    <h3
      style={{
        color: '#ffffff',
        fontWeight: 'normal',
        background: 'linear-gradient(180deg, #00AAE9 30%, #1769A5 100%)',
        borderRadius: '16px',
        padding: '25px',
        fontSize: '14px',
      }}
    ><span style={{fontWeight: 'bold'}}>Fehlendes Merkmal?</span><br />Unsere KI erkennt insgesamt über 40(+) Merkmale und noch mehr …</h3>
    </>
  );
};

const ComposedInvoiceDetailDetections = React.memo(decorate(InvoiceDetailDetections));
export { ComposedInvoiceDetailDetections as InvoiceDetailDetections };
